import { Box, Typography } from "@mui/material";
import { RuleInstance } from "../../types/models/RuleInstance";
import { getParagraphMetadata } from "../../utils/RuleUtil";

interface Props {
  ruleContent: RuleInstance | undefined;
  selectedParagraphIndex: number | null;
  hasError: boolean;
  onSelect: (index: number) => void;
}

export default function ParagraphSelector({
  ruleContent,
  selectedParagraphIndex,
  hasError,
  onSelect,
}: Props) {
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          maxHeight: 500,
          pr: 1,
          border: "1px solid",
          borderColor: "grey.400",
          borderRadius: 1,
        }}
      >
        {!ruleContent ? (
          <Box
            sx={{
              height: "100%",
              minHeight: 420,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <Typography color="text.secondary" variant="body2">
              Select a rule to choose a paragraph
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {ruleContent.Paragraphs?.map((paragraph, index) => {
              const isSelected = selectedParagraphIndex === index;
              const metadata = getParagraphMetadata(paragraph?.Metadata ?? "");
              const paragraphId =
                (metadata?.id?.length ?? 0) > 0 ? metadata?.id[0] : undefined;
              const isSelectable = !!paragraphId;

              return (
                <Box
                  key={index}
                  onClick={() => {
                    isSelectable && onSelect(index);
                  }}
                  sx={{
                    cursor: isSelectable ? "pointer" : "not-allowed",
                    opacity: isSelectable ? 1 : 0.45,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                    borderLeft: 3,
                    borderColor: isSelected ? "primary.main" : "transparent",
                    backgroundColor: isSelected ? "primary.50" : "transparent",
                    "&:hover": {
                      backgroundColor: isSelectable
                        ? isSelected
                          ? "primary.100"
                          : "grey.100"
                        : "transparent",
                    },
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 24,
                      minHeight: 24,
                      borderRadius: "50%",
                      backgroundColor: isSelected ? "primary.main" : "grey.400",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: "bold",
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    {!isSelectable && (
                      <Typography variant="caption" color="text.disabled">
                        Not available — missing metadata
                      </Typography>
                    )}
                    <Typography
                      variant="body1"
                      sx={{ color: isSelected ? "primary.main" : "text.primary", mt: 0.25 }}
                      style={{
                        display: "-webkit-box",
                        overflow: "hidden",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {paragraph.TextContent}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      {hasError && (
        <Typography sx={{ color: "#cf322c", mt: 1, fontSize: "0.8rem" }}>
          A paragraph must be selected.
        </Typography>
      )}
    </Box>
  );
}
